import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import MuiLink from '@mui/material/Link';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';

import {
  ControlledCheckbox,
  ControlledFileUpload,
  ControlledTextField,
  SectionErrorSummary,
  SectionTitle,
} from 'modules/shared/components';
import { ensureFileId } from 'modules/shared/service';
import { EmployeeCredentialsModal, InlineRejectSection } from '../components';
import { generateEmployeeCredentials } from '../interview.service';

/* =========================== Constants & Enums =========================== */

const CURRENT_ROUND_KEY = 'induction';

const INDUCTION_ACTION = {
  COMPLETE: 'complete',
  REJECT: 'reject',
};

const JOINING_KIT_STATUS = {
  COMPLETED: 'completed',
};

const VALIDATION_MESSAGES = {
  BIOMETRICS_REQUIRED: 'Biometrics is required',
  WELCOME_KIT_REQUIRED: 'Welcome kit is required',
  COMPANY_POLICY_REQUIRED: 'Company policy kit is required',
  HELMET_RAINCOAT_REQUIRED: 'Helmet / Raincoat is required',
};

/* =========================== Helper Functions =========================== */

function createDraftKit() {
  return {
    name: '',
    file: null,
  };
}

function hasEmptyDraft(draftKits) {
  return draftKits.some((k) => !k.name?.trim() && !k.file);
}

/**
 * Induction round for completing employee onboarding requirements.
 *
 * @param {Object} props
 * @param {Object} props.data - Induction data returned from backend.
 * @param {Object} props.interviewData - Interview entity for the candidate.
 * @param {boolean} props.readOnly - When true, disables all interactions.
 * @param {Function} [props.onApprove] - Called when induction is completed.
 * @param {Function} [props.onReject] - Called when candidate is rejected.
 */
export default function InductionRound({ data, interviewData, readOnly, onApprove, onReject }) {
  const [actionMode, setActionMode] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);

  const [generatingCredentials, setGeneratingCredentials] = useState(false);

  const backendJoiningKit = useMemo(() => data?.joiningKit ?? [], [data?.joiningKit]);

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      biometrics: data?.biometrics ?? false,
      welcomeKit: false,
      companyPolicyKit: false,
      helmetRaincoat: false,
      draftKits: [],
      rejectionReason: '',
    },
  });
  const {
    fields: draftFields,
    append,
    remove,
  } = useFieldArray({
    control: methods.control,
    name: 'draftKits',
  });

  const { watch, trigger, resetField } = methods;

  const biometrics = watch('biometrics');
  const welcomeKit = watch('welcomeKit');
  const companyPolicyKit = watch('companyPolicyKit');
  const helmetRaincoat = watch('helmetRaincoat');
  const rejectionReason = watch('rejectionReason');
  const draftKits = watch('draftKits');

  const allPredefinedChecked = welcomeKit && companyPolicyKit && helmetRaincoat;

  const canSubmit = biometrics && allPredefinedChecked && credentials;

  const isReadonly = readOnly;

  const handleGenerateCredentials = async () => {
    try {
      setGeneratingCredentials(true);

      const data = await generateEmployeeCredentials(interviewData.id);
      setCredentials(data);
    } catch (err) {
      toast.error(err.message || 'Failed to generate employee credentials');
    } finally {
      setGeneratingCredentials(false);
    }
  };

  const resetAction = () => {
    setActionMode(null);
    resetField('rejectionReason');
  };

  const handleSubmit = async () => {
    const isValid = await trigger(
      ['biometrics', 'welcomeKit', 'companyPolicyKit', 'helmetRaincoat'],
      { shouldFocus: true },
    );

    if (!isValid || !canSubmit) return;

    const draftKitsValue = methods.getValues('draftKits');

    const validDrafts = [];

    for (let i = 0; i < draftKitsValue.length; i++) {
      const { name, file } = draftKitsValue[i];

      if (!name && !file) continue;

      if (!name || !file) {
        if (!name) {
          methods.setError(`draftKits.${i}.name`, {
            type: 'manual',
            message: 'Document name is required',
          });
        }
        if (!file) {
          methods.setError(`draftKits.${i}.file`, {
            type: 'manual',
            message: 'Document file is required',
          });
        }
        return;
      }

      validDrafts.push({ name: name.trim(), file });
    }

    const uploadedJoiningKit = await Promise.all(
      validDrafts.map(async ({ name, file }) => ({
        name,
        id: await ensureFileId(file),
        status: JOINING_KIT_STATUS.COMPLETED,
        handoverDate: new Date().toISOString(),
      })),
    );

    onApprove?.({
      action: INDUCTION_ACTION.COMPLETE,
      induction: {
        biometrics: true,
        joiningKit: uploadedJoiningKit,
      },
      credentials: {
        employeeId: credentials?.employeeId,
        password: credentials?.password,
      },
      currentRoundKey: CURRENT_ROUND_KEY,
    });
  };

  const handleReject = async () => {
    const isValid = await trigger('rejectionReason', { shouldFocus: true });
    if (!isValid) return;

    onReject?.({
      action: INDUCTION_ACTION.REJECT,
      rejectionReason,
    });
  };

  const existingNames = useMemo(
    () =>
      new Set([
        ...backendJoiningKit.map((k) => k.name),
        ...draftKits.map((k) => k.name).filter(Boolean),
      ]),
    [backendJoiningKit, draftKits],
  );

  return (
    <>
      <FormProvider {...methods}>
        <form className='column gap-4'>
          <SectionTitle
            title='INDUCTION'
            action={
              !isReadonly && (
                <button
                  type='button'
                  className='btn text-btn'
                  disabled={hasEmptyDraft(draftKits)}
                  onClick={() => {
                    if (hasEmptyDraft(methods.getValues('draftKits'))) return;
                    append(createDraftKit());
                  }}
                >
                  + Add document
                </button>
              )
            }
          />

          <SectionErrorSummary
            names={['biometrics', 'welcomeKit', 'companyPolicyKit', 'helmetRaincoat']}
          />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 6,
            }}
          >
            <div className='data-grid-column'>
              <PredefinedKitCheckbox
                id='biometrics'
                name='biometrics'
                label='Add Biometrics'
                rules={{ validate: (v) => v || VALIDATION_MESSAGES.BIOMETRICS_REQUIRED }}
                disabled={isReadonly}
              />

              <PredefinedKitCheckbox
                id='welcomeKit'
                name='welcomeKit'
                label='Welcome kit'
                rules={{ validate: (v) => v || VALIDATION_MESSAGES.WELCOME_KIT_REQUIRED }}
                disabled={isReadonly}
              />

              <PredefinedKitCheckbox
                id='companyPolicyKit'
                name='companyPolicyKit'
                label='Company policy kit'
                rules={{ validate: (v) => v || VALIDATION_MESSAGES.COMPANY_POLICY_REQUIRED }}
                disabled={isReadonly}
              />

              <PredefinedKitCheckbox
                id='helmetRaincoat'
                name='helmetRaincoat'
                label='Helmet / Raincoat'
                rules={{ validate: (v) => v || VALIDATION_MESSAGES.HELMET_RAINCOAT_REQUIRED }}
                disabled={isReadonly}
              />
            </div>

            <div className='column'>
              {backendJoiningKit.map((item) => (
                <JoiningKitRow
                  key={item.name}
                  name={item.name}
                  backendFile={{ id: item.id, url: item.url }}
                  readOnly
                />
              ))}

              {!isReadonly &&
                draftFields.map((field, index) => (
                  <DraftKitRow
                    key={field.id}
                    index={index}
                    existingNames={existingNames}
                    onRemove={() => remove(index)}
                  />
                ))}

              <EmployeeIdSection
                isReadonly={isReadonly}
                generating={generatingCredentials}
                onGenerate={handleGenerateCredentials}
                credentials={credentials}
                onView={() => setShowCredentialsModal(true)}
              />
            </div>
          </Box>

          {!readOnly && (
            <div className='column'>
              <div className='row row--end gap-5'>
                {actionMode !== 'reject' && (
                  <button
                    type='button'
                    className='btn text-btn text-btn--reject'
                    onClick={() => setActionMode('reject')}
                    disabled={!!actionMode}
                  >
                    Reject candidate
                  </button>
                )}
                <button
                  type='button'
                  className='btn cta-btn'
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                >
                  Submit & Complete Recruitment
                </button>
              </div>
              {actionMode === 'reject' && (
                <InlineRejectSection
                  name='rejectionReason'
                  onCancel={resetAction}
                  onConfirm={handleReject}
                />
              )}
            </div>
          )}
        </form>
      </FormProvider>
      <EmployeeCredentialsModal
        open={showCredentialsModal}
        onClose={() => setShowCredentialsModal(false)}
        data={credentials}
      />
    </>
  );
}

function PredefinedKitCheckbox({ id, name, label, rules, disabled }) {
  return (
    <label htmlFor={id} className='data-grid-row items-center'>
      <span className='label--secondary'>{label}</span>
      <ControlledCheckbox
        id={id}
        name={name}
        rules={rules}
        disabled={disabled}
        variant='secondary'
        wrapperSx={{ alignItems: 'flex-end' }}
        showErrorInline={false}
      />
    </label>
  );
}

function EmployeeIdSection({ credentials, isReadonly, generating, onGenerate, onView }) {
  return (
    <div className='row row--between'>
      <span>Employee ID</span>

      <div className='row gap-3'>
        {credentials && (
          <button type='button' className='btn text-btn underline' onClick={onView}>
            view
          </button>
        )}
        <button
          type='button'
          className='btn cta-outlined-btn'
          onClick={onGenerate}
          disabled={isReadonly || generating || Boolean(credentials)}
        >
          Generate
        </button>
      </div>
    </div>
  );
}

function DraftKitRow({ index, existingNames, onRemove }) {
  const {
    formState: { errors },
  } = useFormContext();

  const nameError = errors?.draftKits?.[index]?.name?.message;
  const fileError = errors?.draftKits?.[index]?.file?.message;

  return (
    <div>
      <div className='row row--between gap-3'>
        <ControlledTextField
          name={`draftKits.${index}.name`}
          placeholder='Document name'
          rules={{
            minLength: { value: 3, message: 'Document Name must be at least 3 characters' },
            maxLength: { value: 50, message: 'File Name must be at most 50 characters' },
            validate: (v) => {
              if (!v) return true;
              const trimmed = v.trim();

              const namesExcludingSelf = Array.from(existingNames).filter((n) => n !== trimmed);

              return (
                !namesExcludingSelf.includes(trimmed) || 'A document with this name already exists'
              );
            },
          }}
          fullWidth
          sx={{ maxWidth: '50%' }}
          showErrorInline={false}
        />

        <div className='row gap-3'>
          <ControlledFileUpload
            name={`draftKits.${index}.file`}
            autoUpload={false}
            showPreview
            previewPosition='before'
          />

          <button type='button' className='btn text-btn' onClick={onRemove} title='Remove document'>
            <Icon icon='basil:trash-outline' width={22} />
          </button>
        </div>
      </div>
      <InlineErrorBadge message={nameError || fileError} />
    </div>
  );
}

function InlineErrorBadge({ message }) {
  if (!message) return null;

  return <FormHelperText error>{message}</FormHelperText>;
}

/**
 * Displays a joining kit document row.
 *
 * @param {Object} props
 * @param {string} props.name - Document name.
 * @param {{id: string, url: string}} [props.backendFile] - File from backend.
 * @param {File} [props.pendingFile] - File pending upload.
 * @param {boolean} props.readOnly - Disables actions.
 * @param {Function} [props.onRemove] - Removes pending document.
 */
function JoiningKitRow({ name, backendFile, pendingFile, readOnly = false, onRemove = () => {} }: { name: any; backendFile: any; pendingFile?: any; readOnly?: any; onRemove?: any }) {
  const previewUrl = backendFile?.url
    ? backendFile.url
    : pendingFile instanceof File
      ? URL.createObjectURL(pendingFile)
      : null;

  useEffect(() => {
    return () => {
      if (pendingFile instanceof File && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [pendingFile, previewUrl]);

  return (
    <div className='data-grid-row row--between'>
      <span>{name}</span>

      <div className='row gap-3 justify-self-end'>
        {previewUrl && (
          <MuiLink
            href={previewUrl}
            target='_blank'
            rel='noreferrer'
            sx={{ textDecoration: 'underline' }}
          >
            view
          </MuiLink>
        )}
      </div>
    </div>
  );
}
